import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * This is an internal component that all remaining buttons build upon. It exposes props
 * that should not appear in the public interface but allow us to easily create new and
 * more specific button variants.
 */
import React from 'react';
import MUIButton, { buttonClasses } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { exhaustiveCheck } from '@famly/stat_ts-utils_exhaustive-check';
import { extractSystemStyles, handlePassthroughSx } from '@famly/mf_system_system-props';
import { useTooltipRequiredProps } from '@famly/mf_data-display_tooltip';
import { useAriaAndDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { Icon } from '@famly/mf_data-display_icon';
import { useResponsiveSize } from '@famly/mf_util_breakpoints';
// The React Compiler cannot lower a template-literal computed object key, so this
// dynamic MUI-class selector is hoisted to module scope and used as a plain
// identifier key (which compiles).
const startIconSelector = `.${buttonClasses.startIcon}`;
export const ButtonBase = ({ text, onClick, onPointerEnter, onPointerLeave, onTouchCancel, onTouchStart, onTouchEnd, onMouseOver, onMouseOut, onMouseEnter, onMouseLeave, isDisabled, tabIndex, variant, size: sizeFromProps, fullWidth, type, icon, isLoading, sx, endIcon, form, ref, ...rest }) => {
    var _a;
    const isIconOnlyButton = hasValue(icon) && !hasValue(text);
    // 'mini' is a fixed size for icon-only buttons and never follows the viewport. The types keep it
    // off text buttons, but untyped callers (Storybook controls, say) can still pass it, so a text
    // button falls back to its usual size.
    const isMini = sizeFromProps === 'mini' && isIconOnlyButton;
    const responsiveSize = useResponsiveSize(sizeFromProps === 'mini' ? undefined : sizeFromProps);
    const size = isMini ? 'mini' : responsiveSize;
    const ariaAndDataProps = useAriaAndDataProps(rest);
    const tooltipProps = useTooltipRequiredProps(rest);
    const anchorProps = rest.href
        ? { href: rest.href, target: rest.target, rel: (_a = rest.rel) !== null && _a !== void 0 ? _a : (rest.target === '_blank' ? 'noopener' : undefined) }
        : undefined;
    const iconSize = getIconSize(size);
    const commonProps = {
        onClick,
        onPointerEnter,
        onPointerLeave,
        onTouchCancel,
        onTouchStart,
        onTouchEnd,
        onMouseOver,
        onMouseOut,
        onMouseEnter,
        onMouseLeave,
        disabled: isDisabled || isLoading,
        tabIndex,
        startIcon: icon ? getStartIcon(icon, isLoading, iconSize, size) : undefined,
        size,
        fullWidth,
        type,
        ref,
        form,
        ['aria-label']: rest['aria-label'],
    };
    const { sx: systemSx } = extractSystemStyles(rest);
    if (isIconOnlyButton) {
        return (_jsx(MUIButton, { variant: variant, sx: theme => ({
                ...systemSx(theme),
                borderRadius: '50% !important',
                width: `${getIconButtonWidth(size)} !important`,
                minWidth: 'unset',
                [startIconSelector]: {
                    margin: `${theme.modernFamlyTheme.spacing(0)}`,
                },
            }), ...commonProps, ...anchorProps, ...ariaAndDataProps, ...tooltipProps }));
    }
    return (_jsxs(MUIButton, { variant: variant, endIcon: endIcon, sx: [...handlePassthroughSx(sx), ...handlePassthroughSx(systemSx)], ...commonProps, ...anchorProps, ...ariaAndDataProps, ...tooltipProps, children: [text, _jsx(LoadingOverlay, { isLoading: isLoading && !hasValue(icon) })] }));
};
ButtonBase.displayName = 'ButtonBase';
const getStartIcon = (buttonIconProps, isLoading, iconSize, size) => {
    if (isLoading) {
        // The spinner and its padding fill exactly the icon's box, so the button keeps its size while loading.
        // A mini button's icon is too small to spare the padding.
        const isMini = size === 'mini';
        return (_jsx(Stack, { padding: isMini ? 0 : 0.5, justifyContent: "center", alignItems: "center", children: _jsx(CircularProgress, { size: isMini ? iconSize : iconSize - 4 }) }));
    }
    const iconProps = typeof buttonIconProps === 'string' ? { name: buttonIconProps } : buttonIconProps;
    return _jsx(Icon, { ...iconProps, size: iconSize });
};
const getIconSize = (size) => {
    switch (size) {
        case 'mini':
            return 16;
        case 'compact':
            return 18;
        case 'regular':
            return 24;
        default:
            exhaustiveCheck(size);
            return 24;
    }
};
const getIconButtonWidth = (size) => {
    switch (size) {
        case 'mini':
            return '24px';
        case 'compact':
            return '36px';
        case 'regular':
            return '48px';
        default:
            exhaustiveCheck(size);
            return '48px';
    }
};
const LoadingOverlayContainer = styled('div') `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: transparent;
    /*
     * The overlay is always rendered (empty when not loading) and, being absolutely
     * positioned, paints on top of the button's static content — including the endIcon.
     * Without this it intercepts hit-testing (document.elementFromPoint), so the content
     * beneath is reported as "covered" and the button (e.g. a DropdownButton's chevron)
     * is treated as not visible/clickable. It is purely decorative — the spinner it holds
     * is non-interactive and the button itself is disabled while loading — so it must never
     * capture pointer events.
     */
    pointer-events: none;
`;
const LoadingOverlay = ({ isLoading }) => {
    return _jsx(LoadingOverlayContainer, { children: isLoading ? _jsx(CircularProgress, { size: 24 }) : null });
};
